// ---------------------------------------------------------------------------
// working-hours utilities — the ONE place the Calendar decides whether a
// given appointment interval fits inside a clinic's split shifts.
//
// Mirrors backend `working_hours.day_shifts` + the interval semantics in
// `scheduling_engine.validate_booking_slot` so the frontend banner and the
// backend booking validator NEVER disagree.
//
// Rules (locked down by unit tests in `workingHours.test.js`):
//   • A working day is a set of shifts: `[{open: "HH:MM", close: "HH:MM"}, ...]`.
//   • An appointment fits when its FULL interval [start, end] is contained
//     inside ONE shift:  `start >= shift.open  AND  end <= shift.close`.
//   • Boundary rule: an appointment starting exactly at `shift.open` is
//     valid; an appointment ending exactly at `shift.close` is valid.
//   • The gap between shifts is a BREAK — it stripes the calendar but
//     must NEVER by itself trigger the "outside working hours" banner.
//   • The banner only fires when at least one REAL appointment fails
//     the interval check.
// ---------------------------------------------------------------------------

const DAY_NAMES = ["sunday", "monday", "tuesday", "wednesday",
                    "thursday", "friday", "saturday"];

function parseHM(s, fallback = null) {
  if (!s || typeof s !== "string") return fallback;
  const m = /^(\d{1,2}):(\d{2})$/.exec(s.trim());
  if (!m) return fallback;
  const h = Number(m[1]);
  const min = Number(m[2]);
  if (Number.isNaN(h) || Number.isNaN(min)) return fallback;
  return { h, min };
}

/** Normalise a clinic's `working_hours[day]` config into an ordered list of
 * shifts. Handles the current `{shifts: [{open,close}]}` schema AND the
 * legacy flat `{open, close}` fallback. Malformed / zero-length shifts are
 * silently dropped. */
export function dayShifts(cfg) {
  if (!cfg || cfg.closed) return [];
  const raw = Array.isArray(cfg.shifts) ? cfg.shifts : null;
  const list = raw && raw.length
    ? raw
    : (cfg.open && cfg.close ? [{ open: cfg.open, close: cfg.close }] : []);
  const out = [];
  for (const s of list) {
    const o = parseHM(s?.open, null);
    const c = parseHM(s?.close, null);
    if (!o || !c) continue;
    const openMin = o.h * 60 + o.min;
    const closeMin = c.h * 60 + c.min;
    if (closeMin > openMin) out.push({ open: o, close: c, openMin, closeMin });
  }
  out.sort((a, b) => a.openMin - b.openMin);
  return out;
}

/** Get today's normalised shift list from `working_hours` — safe for weekly
 * schedules keyed by lowercase day name. Returns `[]` for closed days. */
export function shiftsForDay(workingHours, dayDate) {
  if (!workingHours || !dayDate) return [];
  const key = DAY_NAMES[dayDate.getDay()];
  return dayShifts(workingHours[key]);
}

/** Does the given local Date (instantaneous) lie inside ANY shift?
 * Uses `openMin <= t < closeMin` — matches the slot-boundary rule for
 * marking a 30-min slot at time `t` as bookable. */
export function inShifts(shifts, d) {
  if (!shifts?.length) return true;   // no shift config → treat as always in-hours
  const t = d.getHours() * 60 + d.getMinutes();
  for (const s of shifts) {
    if (s.openMin <= t && t < s.closeMin) return true;
  }
  return false;
}

/** Full-INTERVAL check: does the appointment [start, end] fit inside ONE
 * shift? This is the ONLY correct way to decide whether an appointment
 * is "out of hours". Checks:
 *   start >= shift.open  AND  end <= shift.close
 *
 * Boundary examples (all VALID, from the P1 spec):
 *   09:00–13:00 shift · 12:30–13:00 appt → end == close (13:00) → OK
 *   16:00–20:00 shift · 16:00–16:30 appt → start == open  (16:00) → OK
 *   16:00–20:00 shift · 19:30–20:00 appt → end == close (20:00) → OK
 *
 * INVALID examples:
 *   12:30–13:30 appt across 13:00 → straddles break → NOT in any single shift
 *   13:00–13:30 appt inside break → not contained
 *   15:30–16:00 appt before shift 2 → start < 16:00 → NOT in shift 2
 *   20:00–20:30 appt after close → start >= close → NOT in shift 2
 *
 * If no shifts are configured for the day, defaults to `true` (matches
 * the display-slot policy — an unconfigured day is not "out of hours").
 */
export function apptFitsAnyShift(shifts, startDate, endDate) {
  if (!shifts?.length) return true;
  if (!startDate || !endDate) return true;
  const startMin = startDate.getHours() * 60 + startDate.getMinutes();
  // Compute the END minute-of-day the same way. When an appointment ends
  // exactly at midnight (23:00 + 60min → 00:00), we treat it as 1440 so
  // the containment math still works.
  let endMin = endDate.getHours() * 60 + endDate.getMinutes();
  const spansMidnight = endDate.getDate() !== startDate.getDate()
    || endDate.getMonth() !== startDate.getMonth()
    || endDate.getFullYear() !== startDate.getFullYear();
  if (spansMidnight) endMin += 1440;
  if (endMin <= startMin) return false;   // 0-duration / negative
  for (const s of shifts) {
    if (s.openMin <= startMin && endMin <= s.closeMin) return true;
  }
  return false;
}

/** Convenience: given a list of appointments and the shifts for the day,
 * return `true` iff AT LEAST ONE appointment fails the interval check.
 * This is what the banner should hang off — never `slots.some(outOfHours)`
 * which trivially becomes true for any clinic with a break. */
export function hasOutOfHoursAppointment(shifts, appts) {
  if (!shifts?.length || !appts?.length) return false;
  for (const a of appts) {
    const start = new Date(a.start);
    if (Number.isNaN(start.getTime())) continue;
    const dur = Number(a.duration_min) || 30;
    const end = new Date(start.getTime() + dur * 60000);
    if (!apptFitsAnyShift(shifts, start, end)) return true;
  }
  return false;
}

/** Boolean: is a single appointment outside working hours? Same rule as
 * `hasOutOfHoursAppointment` for one appt. */
export function isAppointmentOutOfHours(shifts, appointment) {
  if (!shifts?.length || !appointment) return false;
  const start = new Date(appointment.start);
  if (Number.isNaN(start.getTime())) return false;
  const dur = Number(appointment.duration_min) || 30;
  const end = new Date(start.getTime() + dur * 60000);
  return !apptFitsAnyShift(shifts, start, end);
}
